import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { TierService } from '../services/tier.service';
import type { TierCapability } from '../models/billing.model';

/**
 * Factory function that creates a guard checking the brand's plan tier includes a
 * given capability (e.g. COMPLIANCE, REVIEWS). This is the route-level dual of the
 * sidebar's availability filter (SidebarComponent.isNavItemAvailable): the sidebar
 * HIDES tier-locked modules, so their routes must be blocked too — otherwise a
 * module hidden from the nav stays reachable by typing its URL.
 *
 * Tier is brand-wide and commercial, NOT per-member, so there is deliberately:
 *   - NO Admin/Owner bypass — an Owner on a Starter plan still hasn't paid for the module.
 *   - NO demo bypass — demo follows the demo brand's own tier, exactly like the sidebar.
 *
 * While tier state is still loading we fail OPEN: hasCapability() reports false until
 * loaded, and the authoritative gate is the server-side RequiresCapability field
 * middleware (CAPABILITY_LOCKED). This guard only keeps the URL in lockstep with the nav.
 *
 * Compose alongside permissionGuard, which runs first (identity before commerce):
 *   canActivate: [permissionGuard('ViewCompliance'), capabilityGuard('COMPLIANCE')]
 */
export function capabilityGuard(capability: TierCapability): CanActivateFn {
  return () => {
    const tier = inject(TierService);
    const router = inject(Router);
    const messageService = inject(MessageService);
    const translateService = inject(TranslateService);

    // Unknown (still-loading) tier state fails open; once loaded, the plan must include it.
    if (!tier.isLoaded() || tier.hasCapability(capability)) {
      return true;
    }

    messageService.add({
      severity: 'warn',
      summary: translateService.instant('common.unauthorized'),
      detail: translateService.instant('guards.capabilityLocked')
    });
    return router.createUrlTree(['/home']);
  };
}
